import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import { NotificationService, NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';

import Global from '../../../../store/global-selectors';
import { allocateFulfillmentPlansPromise } from '../../../../store/inventory/inventory.api';
import { messages } from '../messages';
import { Steps } from './constants';

const allFulfillmentPlansAllocated = 'AllFulfillmentPlansAllocated';
const allocated = 'Allocated';

/**
 * @typedef {object} ItemInfo
 * @property {string?} sku
 * @property {string?} name
 * @property {string?} productId
 */

/**
 * @typedef {object} InventoryValidationError
 * @property {string} id - order id
 * @property {string} fulfillmentPlanId
 * @property {string} sku - SKU of item with allocation error
 * @property {string} name - Name of item with allocation error
 * @property {string} productId - Product ID of item with allocation error
 * @property {string} salesOrderNumber
 */

/**
 * Checks inventory validation errors for the given fulfillment plans.
 * @param {Array<string>} currentFulfillmentPlanIds
 * @param {number} shipFromId
 * @param {{[key: string]: ItemInfo}} itemInfoMap
 * @param {{[key: string]: string}} orderNumberMap
 * @returns {Promise<Array<InventoryValidationError>>} - A promise that resolves to an array of inventory validation errors.
 */
const checkInventoryValidationErrors = async (currentFulfillmentPlanIds, shipFromId, itemInfoMap, orderNumberMap) => {
  const allocationResponse = await allocateFulfillmentPlansPromise(currentFulfillmentPlanIds, shipFromId, false).catch(() => {
    NotificationService.showNotification({
      notificationType: NOTIFICATION_TYPE_ERROR,
      title: <FormattedMessage {...messages.handleAllocationErrorTitle} />,
      content: <FormattedMessage {...messages.handleAllocationErrorMessage} />,
    });
  });

  if (allocationResponse?.status === allFulfillmentPlansAllocated) {
    return [];
  }

  const failedFulfillmentPlanIds = new Set(
    allocationResponse?.inventoryAllocationResults
      ?.filter((result) => result.inventoryAllocationStatus !== allocated)
      ?.map((result) => result.fulfillmentPlanId) ?? [],
  );

  return (
    allocationResponse?.inventoryAllocationCheckInfo?.results
      ?.filter((result) => failedFulfillmentPlanIds.has(result.fulfillmentPlanId) && result.hasInsufficientStock)
      ?.map((result) => {
        const fulfillmentPlanItemIdStr = result.fulfillmentPlanItemId.toString();
        const fulfillmentPlanIdStr = result.fulfillmentPlanId.toString();

        const { sku, name, productId } = itemInfoMap[fulfillmentPlanItemIdStr];
        const salesOrderNumber = orderNumberMap[fulfillmentPlanIdStr];

        return {
          id: fulfillmentPlanIdStr,
          fulfillmentPlanId: fulfillmentPlanIdStr,
          sku: sku || result.productId,
          name: name || result.fulfillmentPlanItemId,
          productId,
          salesOrderNumber,
        };
      }) ?? []
  );
};

/**
 * @typedef {object} VerifyInventoryState
 * @property {boolean} canVerifyInventoryContinue
 * @property {() => void} onVerifyInventoryContinue
 * @property {(isRetry?: boolean) => Promise<void>} prepareVerifyInventoryStep
 */

/**
 * @param {object} params
 * @param {Array<string>} params.currentFulfillmentPlanIds
 * @param {boolean} params.inventoryTrackingIsEnabled
 * @param {(boolean) => void} params.setIsLoading
 * @param {Array<InventoryValidationError>} params.problemOrders
 * @param {(number) => void} params.setCurrentStep
 * @param {(problemOrders: Array<InventoryValidationError>) => void} params.setProblemOrders
 * @param {(selectedProblemOrderIds: Array<string>) => void} params.setSelectedProblemOrderIds
 * @param {number} params.shipFromId
 * @returns {VerifyInventoryState} - An object containing state and functions for verifying inventory.
 */
export const useVerifyInventoryState = ({
  currentFulfillmentPlanIds,
  inventoryTrackingIsEnabled,
  setIsLoading,
  problemOrders,
  setCurrentStep,
  setProblemOrders,
  setSelectedProblemOrderIds,
  shipFromId,
}) => {
  const itemInfoByOrderIdMap = useSelector((state) => {
    const orderItems = Global.selectEntitiesFulfillmentPlansItemsByFulfillmentPlanIds(state, {
      fulfillmentPlanIds: currentFulfillmentPlanIds,
    });
    return orderItems.reduce((acc, { fulfillmentPlanItemId, sku, name, productId }) => {
      if (fulfillmentPlanItemId) {
        return { ...acc, [fulfillmentPlanItemId]: { sku, name, productId } };
      }
      return acc;
    }, {});
  });
  const orderNumberByOrderIdMap = useSelector((state) => {
    const orders = Global.selectEntitiesSalesOrdersByFulfillmentPlanIds(state, {
      fulfillmentPlanIds: currentFulfillmentPlanIds,
    });
    return orders.reduce((acc, order) => {
      let updatedAcc = { ...acc };
      if (order.fulfillmentPlanIds) {
        order.fulfillmentPlanIds.forEach((fpid) => {
          updatedAcc = { ...updatedAcc, [fpid]: order?.orderNumber || '' };
        });
      }
      return updatedAcc;
    }, {});
  });
  return useMemo(() => {
    const onVerifyInventoryContinue = () => {
      setCurrentStep(Steps.complete);
    };
    const prepareVerifyInventoryStep = async (isRetry = false) => {
      if (!inventoryTrackingIsEnabled) {
        return;
      }
      setIsLoading(true);
      const inventoryValidationErrors = await checkInventoryValidationErrors(
        currentFulfillmentPlanIds,
        shipFromId,
        itemInfoByOrderIdMap,
        orderNumberByOrderIdMap,
      );
      if (inventoryValidationErrors.length === 0 && !isRetry) {
        // Nothing to do in Verify Inventory step, process complete that step
        onVerifyInventoryContinue();
        return;
      }
      setProblemOrders(inventoryValidationErrors);
      setSelectedProblemOrderIds([]);
      setIsLoading(false);
    };
    const areAllProblemsGone = problemOrders.length === 0;
    const canVerifyInventoryContinue = areAllProblemsGone;
    return {
      canVerifyInventoryContinue,
      onVerifyInventoryContinue,
      prepareVerifyInventoryStep,
    };
  }, [
    currentFulfillmentPlanIds,
    inventoryTrackingIsEnabled,
    itemInfoByOrderIdMap,
    orderNumberByOrderIdMap,
    problemOrders.length,
    setCurrentStep,
    setIsLoading,
    setProblemOrders,
    setSelectedProblemOrderIds,
    shipFromId,
  ]);
};
